import { BadgeLocationData } from '@weavix/models/src/badges/badge-location-data';
import { Person } from 'weavix-shared/models/person.model';
import { Point, Vertices } from 'weavix-shared/models/location.model';
import { Geofence, MapWalt } from 'weavix-shared/models/weavix-map.model';
import { insidePolygon } from 'weavix-shared/utils/polygon';
import { MapUtils } from 'weavix-shared/utils/map';

/** Display name and computed polygon area for one geofence the walt's badge reports being inside. */
export interface GeofenceAreaInfo {
    name?: string;
    area?: number;
}

/**
 * Facilities whose badge-reported location may be shown for a walt: every facility the assigned
 * person belongs to, plus the walt's own assigned facility.
 * @returns null when no person is assigned (or their facilities are unknown), meaning every
 * badge-reported site is allowed.
 */
export function getAllowedFacilityIds(walt: MapWalt, person: Person | null | undefined): Set<string> | null {
    if (!person?.facilities) return null;
    const allowed = new Set(Object.keys(person.facilities).filter(id => person.facilities[id]));
    if (walt.facilityId) allowed.add(walt.facilityId);
    return allowed;
}

/**
 * Whether the badge reports the walt physically inside any allowed site — not just its assigned
 * one. Presence in badge.facilities means inside (entries are removed on facility exit).
 */
export function isOnKnownSite(badge: BadgeLocationData | undefined, allowed: Set<string> | null): boolean {
    return (badge?.facilities ?? []).some(f => !allowed || allowed.has(f.id));
}

/**
 * Picks the geofence to display for a walt: dated entries in allowed sites only, preferring the
 * assigned facility's geofences when the walt is inside one, then the smallest by area. Entries
 * without a computed area (records still loading, or deleted) lose to any entry with one; among
 * equal or unknown areas the last badge entry wins (badge order approximates order of entry but
 * is not guaranteed). Entries missing a facilityId cannot be site-checked and are skipped.
 * @returns the chosen geofence's id and best-known name, or null when nothing qualifies.
 */
export function selectDisplayGeofence(
    badge: BadgeLocationData | undefined,
    allowed: Set<string> | null,
    assignedFacilityId: string | undefined,
    areasById: ReadonlyMap<string, GeofenceAreaInfo>,
): { id: string, name: string | null } | null {
    const candidates = (badge?.geofences ?? []).filter(g => g.date && g.facilityId && (!allowed || allowed.has(g.facilityId)));
    if (!candidates.length) return null;

    const assigned = candidates.filter(g => g.facilityId === assignedFacilityId);
    const pool = assigned.length ? assigned : candidates;

    let bestFence = pool[0];
    let bestArea = areasById.get(pool[0].id)?.area;
    for (const fence of pool.slice(1)) {
        const area = areasById.get(fence.id)?.area;
        const better = area !== undefined
            ? (bestArea === undefined || area <= bestArea)
            : bestArea === undefined;
        if (better) {
            bestFence = fence;
            bestArea = area;
        }
    }

    return { id: bestFence.id, name: areasById.get(bestFence.id)?.name ?? bestFence.name ?? null };
}

/**
 * Planar shoelace area over [longitude, latitude] vertices. The unit is arbitrary (squared
 * degrees) — valid only for comparing geofences at the same physical location, never as a
 * real-world measurement.
 * @returns undefined for anything that isn't a real polygon: fewer than 3 vertices, or a
 * degenerate (zero-area) shape — otherwise a collinear polygon would rank as smallest.
 */
export function polygonArea(vertices: Vertices | undefined): number | undefined {
    if (!vertices || vertices.length < 3) return undefined;
    let sum = 0;
    for (let i = 0; i < vertices.length; i++) {
        const [x1, y1] = vertices[i];
        const [x2, y2] = vertices[(i + 1) % vertices.length];
        sum += x1 * y2 - x2 * y1;
    }
    const area = Math.abs(sum / 2);
    return area > 0 ? area : undefined;
}

/** The location to plot for a walt: its repeater pin when `usePin` is set and the pin is valid, otherwise its calculated location. */
export function getMapLocation(walt: MapWalt, usePin: boolean): Point | undefined {
    const pin = walt.meshRepeater?.location;
    return usePin && MapUtils.isValidLocation(pin) ? pin : walt.location;
}

const METERS_PER_DEGREE = 4.0075e7 / 360;
const PIN_EDGE_TOLERANCE_METERS = 1;

/** True when a [lng, lat] point lies within `toleranceMeters` of any edge of the polygon. */
function isOnPolygonEdge(vertices: Vertices, point: Point, toleranceMeters: number): boolean {
    const [x, y] = point;
    const lngScale = Math.cos(Math.min(89, Math.abs(y)) * Math.PI / 180);
    const tolerance = toleranceMeters / METERS_PER_DEGREE;
    return vertices.some((start, i) => {
        const end = vertices[(i + 1) % vertices.length];
        const dx = (end[0] - start[0]) * lngScale;
        const dy = end[1] - start[1];
        const px = (x - start[0]) * lngScale;
        const py = y - start[1];
        const lengthSquared = dx * dx + dy * dy;
        const t = lengthSquared ? Math.max(0, Math.min(1, (px * dx + py * dy) / lengthSquared)) : 0;
        return (px - t * dx) ** 2 + (py - t * dy) ** 2 <= tolerance ** 2;
    });
}

/**
 * The smallest geofence on the pin's floor that strictly contains the pin or has it within 1 m of an edge, or null when none does.
 * Matches floors as the backend does: a geofence without a floor plan level covers every floor, and a pin without a level is on floor 1.
 */
export function selectPinGeofence(
    pin: Point,
    pinLevel: number | undefined,
    geofences: ReadonlyArray<Pick<Geofence, 'id' | 'name' | 'vertices' | 'floorPlanId'>>,
    floorPlanLevels: ReadonlyMap<string, number>,
): Pick<Geofence, 'id' | 'name'> | null {
    let best: Pick<Geofence, 'id' | 'name'> | null = null;
    let bestArea: number | undefined;
    for (const fence of geofences) {
        const fenceLevel = fence.floorPlanId ? floorPlanLevels.get(fence.floorPlanId) : undefined;
        if (fenceLevel != null && fenceLevel !== (pinLevel ?? 1)) continue;
        const area = polygonArea(fence.vertices);
        if (area === undefined) continue;
        if (!insidePolygon(fence.vertices, pin, true) && !isOnPolygonEdge(fence.vertices, pin, PIN_EDGE_TOLERANCE_METERS)) continue;
        if (bestArea === undefined || area < bestArea) {
            best = { id: fence.id, name: fence.name };
            bestArea = area;
        }
    }
    return best;
}

/** Formats a [longitude, latitude] point as "latitude, longitude" to 6 decimal places. */
export function formatCoordinates(point: Point): string {
    const [lng, lat] = point;
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}
